import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { BowmanCacheService } from 'bowman-cache';
import { Observable, throwError } from 'rxjs';
import { ActionResult, HelperFunctions } from 'bowman-commons';
import { DDefinition } from './model/ddefinition';
import { DictionaryTestRequest } from './model/dictionary-test-request';
import { DictionaryTestResults } from './model/dictionary-test-results';
import { catchError, map, delay } from 'rxjs/operators';
import { DData } from './model/ddata';
import { DDataDto } from './model/ddata-dto';
import { FileSaverService } from 'ngx-filesaver';
import { Common } from 'src/app/dashboard/common';
import { ImportResponse } from 'src/app/import-export/model/import-response';
import { ImportJsonRequest } from 'src/app/import-export/model/import-json-request';


@Injectable({
  providedIn: 'root'
})
export class DictionaryService {

  private _mainUrl: string = window.location.pathname + '/api/dictionary/';

  headers: HttpHeaders = new HttpHeaders();
  constructor(private _http: HttpClient, private _cacheService: BowmanCacheService, private _fileSaverService: FileSaverService) {
    this.headers = new HttpHeaders().append('Content-Type', 'application/json').append('Accept', 'application/json');
  }


  public listDefinitions(): Observable<ActionResult<Array<DDefinition>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'definition/list');
    this._cacheService.flushServiceCache(this, 'listDefinitions');
    return this._http.get<ActionResult<Array<DDefinition>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public createDefinition(ddefinition: DDefinition): Observable<ActionResult<string>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'definition/insert');
    this._cacheService.flushServiceCache(this, 'listDefinitions');
    return this._http.post<ActionResult<string>>(dataUrl, ddefinition, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public updateDefinition(ddefinition: DDefinition): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'definition/update');
    this._cacheService.flushServiceCache(this, 'listDefinitions');
    return this._http.put<ActionResult<boolean>>(dataUrl, ddefinition, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  // Delete
  public deleteData(dictionaryName: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/' + dictionaryName);
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public deleteDefinition(id: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'definition/' + id);
    this._cacheService.flushServiceCache(this, 'listDefinitions');
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }




  public readDictionaryData(name: string): Observable<ActionResult<DData>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/read/' + name);
    return this._http.get<ActionResult<DData>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public createDictionaryData(ddata: DDataDto): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/insert');
    return this._http.post<ActionResult<boolean>>(dataUrl, ddata, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public updateDictionaryData(ddata: DDataDto): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/update');
    return this._http.put<ActionResult<boolean>>(dataUrl, ddata, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public deleteDictionaryData(id: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/' + id);
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }


  // Test Dictionary
  public testDictionaryData(data: DictionaryTestRequest): Observable<ActionResult<DictionaryTestResults>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/test');
    return this._http.post<ActionResult<DictionaryTestResults>>(dataUrl, data, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public readAsCsv(dictionaryName: string): Observable<any> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/readAsCsv/' + dictionaryName);
    const headers = new HttpHeaders().append('Content-Type', 'text/csv').append('Accept', 'text/csv');

    return this._http.get(dataUrl, { headers: headers, observe: "response", responseType: 'blob' }).pipe(map((response: HttpResponse<Blob>) => {
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${dictionaryName}.csv`
      return this._fileSaverService.save(response.body, filename);
    }), catchError(this.handleError));
  }

  public saveAsCsv(dictionaryName: string, base64Content: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'data/appendAsCsv/' + dictionaryName + "/true");
    const headers = new HttpHeaders().append('Content-Type', 'application/octet-stream').append('Accept', 'application/json');
    const decodedContent: string = this.decodeBase64(base64Content);
    return this._http.post<ActionResult<boolean>>(dataUrl, decodedContent, { headers: headers }).pipe(catchError(this.handleError));
  }
  public importDefinition(object: ImportJsonRequest): Observable<ActionResult<Array<ImportResponse>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'definition/import');
    return this._http.post<ActionResult<any>>(dataUrl, object, { headers: this.headers }).pipe(catchError(this.handleError));
  }
  private decodeBase64(base64String) {
    try {
      // Utilizza la funzione atob() per decodificare la stringa Base64
      const decodedString = atob(base64String);
      return decodedString;
    } catch (error) {
      console.error("Errore durante la decodifica Base64:", error);
      return null; // Restituisce null in caso di errore
    }
  }

  // Funzione per gestire l'input e l'output
  private processInput() {
    const inputBase64 = (document.getElementById("base64Input") as any).value;
    const outputElement = document.getElementById("output");

    if (!inputBase64) {
      outputElement.textContent = "Inserisci una stringa Base64 da decodificare.";
      return;
    }

    const decodedText = this.decodeBase64(inputBase64);
    if (decodedText) {
      outputElement.textContent = "Testo decodificato: " + decodedText;
    } else {
      outputElement.textContent = "Impossibile decodificare la stringa Base64. Verifica che sia corretta.";
    }
  }

  private handleError(error: any) {
    let parsedError: any;
    if (error && error.error && error.error.message) {
      parsedError = error.error.message;
    } else if (error && error.message) {
      parsedError = error.message;
    } else if (error && error._body) {
      parsedError = error._body;
    } else {
      parsedError = 'Dictionary Manager Service Error';
    }
    return throwError(parsedError);
  }

}
